/**
 * Componente OptimizedImage
 *
 * Imagem otimizada com:
 * - Dimensões explícitas (evita CLS)
 * - Lazy loading inteligente
 * - Variantes Sharp (AVIF/WebP/JPG) quando imageId existir
 * - Transformações Cloudinary + srcset quando a URL for Cloudinary
 * - URLs de iili.io / freeimage.host / postimg / uploads locais sem alteração
 */

import React from 'react';
import {
  CATALOG_SRCSET_WIDTHS,
  getOptimizedImageSrc,
  getResponsiveImageProps,
  isCloudinaryUrl,
} from '../../utils/cloudinaryImage';

const OptimizedImage = ({
  src,
  alt,
  width = 250,
  height = 200,
  className = '',
  priority = false,
  objectFit = 'contain',
  style = {},
  productId,
  imageId,
  originalUrl,
  sizes: sizesProp,
  wrapped = true,
  srcSetWidths = CATALOG_SRCSET_WIDTHS,
  fallbackWidth,
  ...props
}) => {
  const apiUrl =
    process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || '';

  const hasOptimizedImages = Boolean(String(imageId || '').trim());
  const fallbackSrc = originalUrl || src;
  const sizes =
    sizesProp ||
    '(max-width: 640px) 250px, (max-width: 1024px) 500px, 800px';
  const displayFallbackWidth = fallbackWidth || 500;

  const getOptimizedUrl = (size, format) => {
    if (!hasOptimizedImages) return null;
    return `${apiUrl}/api/images/optimized/${encodeURIComponent(imageId)}/${size}.${format}`;
  };

  const containerStyle = {
    width: '100%',
    height: `${Number(height) || 200}px`,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '10px',
    flexShrink: 0,
  };

  const imgStyle = wrapped
    ? {
        width: '100%',
        height: '100%',
        maxHeight: `${Number(height) || 200}px`,
        objectFit: objectFit,
        display: 'block',
        ...style,
      }
    : {
        objectFit: objectFit,
        ...style,
      };

  const loading = priority ? 'eager' : 'lazy';
  const fetchPriority = priority ? 'high' : 'auto';

  const wrap = (node) =>
    wrapped ? (
      <div className="optimized-image-container" style={containerStyle}>
        {node}
      </div>
    ) : (
      node
    );

  const handleFallbackError = (event) => {
    if (fallbackSrc && event.currentTarget.src !== fallbackSrc) {
      event.currentTarget.src = fallbackSrc;
      event.currentTarget.removeAttribute('srcset');
    }
  };

  if (hasOptimizedImages) {
    const sharpFallback = isCloudinaryUrl(fallbackSrc)
      ? getOptimizedImageSrc(fallbackSrc, { width: displayFallbackWidth })
      : fallbackSrc;

    return wrap(
      <picture>
        <source
          srcSet={`
              ${getOptimizedUrl('thumb', 'avif')} 250w,
              ${getOptimizedUrl('small', 'avif')} 500w,
              ${getOptimizedUrl('medium', 'avif')} 800w,
              ${getOptimizedUrl('large', 'avif')} 1200w
            `}
          sizes={sizes}
          type="image/avif"
        />
        <source
          srcSet={`
              ${getOptimizedUrl('thumb', 'webp')} 250w,
              ${getOptimizedUrl('small', 'webp')} 500w,
              ${getOptimizedUrl('medium', 'webp')} 800w,
              ${getOptimizedUrl('large', 'webp')} 1200w
            `}
          sizes={sizes}
          type="image/webp"
        />
        <img
          {...props}
          srcSet={`
              ${getOptimizedUrl('thumb', 'jpg')} 250w,
              ${getOptimizedUrl('small', 'jpg')} 500w,
              ${getOptimizedUrl('medium', 'jpg')} 800w,
              ${getOptimizedUrl('large', 'jpg')} 1200w
            `}
          sizes={sizes}
          src={getOptimizedUrl('medium', 'jpg')}
          alt={alt}
          width={width}
          height={height}
          className={className}
          loading={loading}
          fetchPriority={fetchPriority}
          decoding="async"
          style={imgStyle}
          data-product-id={productId || undefined}
          onError={(event) => {
            if (sharpFallback && event.currentTarget.src !== sharpFallback) {
              event.currentTarget.src = sharpFallback;
              event.currentTarget.removeAttribute('srcset');
            }
          }}
        />
      </picture>
    );
  }

  const cloudinary = getResponsiveImageProps(fallbackSrc, {
    widths: srcSetWidths,
    fallbackWidth: displayFallbackWidth,
  });

  if (cloudinary.canOptimize) {
    return wrap(
      <img
        {...props}
        src={cloudinary.src}
        srcSet={cloudinary.srcSet}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        style={imgStyle}
        data-product-id={productId || undefined}
        onError={handleFallbackError}
      />
    );
  }

  return wrap(
    <img
      {...props}
      src={fallbackSrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading={loading}
      fetchPriority={fetchPriority}
      decoding="async"
      style={imgStyle}
      data-product-id={productId || undefined}
    />
  );
};

export default OptimizedImage;
